import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../../../utils/context/DealContext.tsx';
import { getBidGuidanceValues } from '../utils/getBidGuidanceValues.ts';

const styles: { [key: string]: SxProps<Theme> } = {
  container: { display: 'flex', flexDirection: 'column', gap: theme => theme.spacing(1) },
  guidanceItemContent: {
    alignItems: 'center',
    backgroundColor: 'white',
    border: '1px solid',
    borderColor: 'divider',
    borderTop: 'none',
    display: 'flex',
    justifyContent: 'center',
    padding: theme => `${theme.spacing(2)} 0`,
  },
  guidanceItemHeading: {
    color: theme => theme.palette.primary.contrastText,
    fontSize: '11px',
    fontWeight: 'fontWeightBold',
    padding: theme => `${theme.spacing(0.25)} 0`,
    textTransform: 'uppercase',
  },
};

export const DealForecastBidGuidance = () => {
  const { t } = useTranslation();

  const { useDealForecast } = useDealContext();
  const {
    forecastState: { forecast },
  } = useDealForecast({ shallow: true });

  const {
    high: bidGuidanceHigh,
    low: bidGuidanceLow,
    recommended: bidGuidanceRecommended,
  } = useMemo(() => {
    return getBidGuidanceValues(forecast?.bid_guidance_low, forecast?.bid_guidance_high);
  }, [forecast?.bid_guidance_high, forecast?.bid_guidance_low]);

  return (
    <Box sx={styles.container}>
      <Typography variant="h3" data-test="bid-guidance-label">
        {t('Bid Guidance')}
      </Typography>
      <Grid container data-test="bid-guidance-grid">
        <Grid size={4}>
          <Typography
            data-test="bid-guidance-low-header"
            sx={{
              textAlign: 'center',
              ...styles.guidanceItemHeading,
              backgroundColor: 'charts.canary',
              marginRight: '1px',
            }}
          >
            {t('low')}
          </Typography>
          <Box sx={{ ...styles.guidanceItemContent, borderRight: 'none' }}>
            <Typography variant="body2" sx={{ marginRight: '1px' }} data-test="bid-guidance-low-value">
              {bidGuidanceLow}
            </Typography>
          </Box>
        </Grid>
        <Grid size={4}>
          <Typography
            data-test="bid-guidance-recommended-header"
            sx={{
              textAlign: 'center',
              ...styles.guidanceItemHeading,
              backgroundColor: 'charts.lightBlue',
            }}
          >
            {t('recommended')}
          </Typography>
          <Box sx={{ ...styles.guidanceItemContent, borderColor: 'secondary.main' }}>
            <Typography variant="body2Bold" data-test="bid-guidance-recommended-value">
              {bidGuidanceRecommended}
            </Typography>
          </Box>
        </Grid>
        <Grid size={4}>
          <Typography
            data-test="bid-guidance-high-header"
            sx={{
              textAlign: 'center',
              ...styles.guidanceItemHeading,
              backgroundColor: 'charts.peach',
              marginLeft: '1px',
            }}
          >
            {t('high')}
          </Typography>
          <Box sx={{ ...styles.guidanceItemContent, borderLeft: 'none' }}>
            <Typography variant="body2" sx={{ marginLeft: '1px' }} data-test="bid-guidance-high-value">
              {bidGuidanceHigh}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
